import { useCallback, useMemo } from 'react';
import { toast } from '@cyphex/ui';
import type { ConversationId, CreateReminderRequest, Message } from '@cyphex/types';
/** Deep imports: a store of a few lines and a pure helper, not the task form. */
import { taskComposer } from '@/features/tasks/composerStore';
import { seedFromMessage } from '@/features/tasks/lib/taskForm';
import { copyPermalink } from '../lib/permalink';
import type { MessageExtras } from '../types';
import type { MessageActions } from './useMessageActions';
import { useMarkUnread } from './useMarkUnread';
import { useQuickReactions } from './useQuickReactions';
import { useReminderActions } from './useReminders';
import { useToggleSaved } from './useSavedMessages';

/**
 * Assembles every papercut affordance a message row needs into ONE memoised
 * object.
 *
 * It exists so `ThreadPane` stays a shell: the seven callbacks below would
 * otherwise be seven more props threaded through two memoised components, and
 * any one of them changing identity per render would defeat the memoisation
 * that keeps a 50 000-message timeline cheap.
 */
export function useMessageExtras(
  conversationId: ConversationId | null,
  actions: MessageActions,
  onCustomiseQuickReactions: () => void,
  /** `tasks`: offer "Create a task from this message". Only where the shell is mounted. */
  options: { tasks?: boolean } = {},
): MessageExtras {
  const offerTasks = options.tasks === true;
  const { emoji } = useQuickReactions();
  const toggleSaved = useToggleSaved(conversationId);
  const reminders = useReminderActions(conversationId);
  const markUnread = useMarkUnread();

  const onQuickReact = useCallback(
    (message: Message, value: string) => actions.toggleReaction(message, value),
    [actions],
  );

  const onSetReminder = useCallback(
    (message: Message, request: CreateReminderRequest) => reminders.set(message, request),
    [reminders],
  );

  const onClearReminder = useCallback(
    (message: Message) => reminders.clear(message),
    [reminders],
  );

  const onCopyLink = useCallback(
    (message: Message) => {
      if (!conversationId) return;
      void copyPermalink(conversationId, message.id).then((ok) => {
        if (ok) toast.success('Link copied.');
        else toast.error('Could not copy that link.');
      });
    },
    [conversationId],
  );

  const onMarkUnread = useCallback(
    (message: Message) => {
      if (!conversationId) return;
      markUnread.fromMessage(conversationId, message);
    },
    [conversationId, markUnread],
  );

  /* The task opens with the message's words and a link back to it. Who it is
     for is left to the person making it: the message may be somebody asking
     them, or them asking somebody. */
  const onCreateTask = useCallback(
    (message: Message) => {
      if (!conversationId) return;
      taskComposer.open(
        seedFromMessage(message.bodyText, { messageId: message.id, conversationId }),
      );
    },
    [conversationId],
  );

  return useMemo(
    () => ({
      quickReactions: emoji,
      onQuickReact,
      onToggleSaved: toggleSaved,
      onSetReminder,
      onClearReminder,
      onCopyLink,
      onMarkUnread,
      onCustomiseQuickReactions,
      ...(offerTasks ? { onCreateTask } : {}),
    }),
    [
      emoji,
      offerTasks,
      onClearReminder,
      onCopyLink,
      onCreateTask,
      onCustomiseQuickReactions,
      onMarkUnread,
      onQuickReact,
      onSetReminder,
      toggleSaved,
    ],
  );
}
